import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { BookOpen, FileUp, Pencil, Plus, Trash2, X } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@bso/shared";
import { useI18n } from "@/providers/i18n-provider";

type BibleVersionRow = components["schemas"]["BibleVersion"];
type BibleVersionFileRow = components["schemas"]["BibleVersionFile"];
type Catalog = components["schemas"]["BibleVersionCatalog"];

const API_BASE: string = import.meta.env.VITE_API_BASE ?? "http://localhost:3333";

interface VersionFormValues {
  name: string;
  abbr: string;
  languageIso2: string;
  languageIso3: string;
  languageNameEnglish: string;
  languageNameNative: string;
  status: string;
}

const EMPTY_FORM: VersionFormValues = {
  name: "",
  abbr: "",
  languageIso2: "",
  languageIso3: "",
  languageNameEnglish: "",
  languageNameNative: "",
  status: "published",
};

export function BibleVersionManager() {
  const { t } = useI18n();

  const catalogQuery = createApiQuery<Catalog, components["schemas"]["ErrorModel"]>(
    () => ["admin", "bible-versions"],
    async () => api.GET("/api/admin/bible/listAll"),
    () => ({ staleTime: 30_000 }),
  );

  const filesByVersion = createMemo(() => {
    const map = new Map<string, BibleVersionFileRow>();
    for (const f of catalogQuery.data?.files ?? []) {
      map.set(f.bibleVersionId, f);
    }
    return map;
  });

  // Form state: null = closed, "" = creating, otherwise version id being edited
  const [editingId, setEditingId] = createSignal<string | null>(null);
  const [form, setForm] = createSignal<VersionFormValues>(EMPTY_FORM);
  const [uploadingId, setUploadingId] = createSignal<string | null>(null);
  const [errorMsg, setErrorMsg] = createSignal<string | null>(null);

  const createMutation = createApiMutation(
    async (vars: components["schemas"]["BibleVersionCreateDto"]) =>
      api.POST("/api/admin/bible/create", { body: vars }),
  );
  const updateMutation = createApiMutation(
    async (vars: components["schemas"]["BibleVersionUpdateDto"]) =>
      api.POST("/api/admin/bible/update", { body: vars }),
  );
  const deleteMutation = createApiMutation(async (vars: { id: string }) =>
    api.POST("/api/admin/bible/delete", { body: vars }),
  );

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId("");
    setErrorMsg(null);
  };

  const openEdit = (version: BibleVersionRow) => {
    setForm({
      name: version.name,
      abbr: version.abbr,
      languageIso2: version.languageIso2,
      languageIso3: version.languageIso3,
      languageNameEnglish: version.languageNameEnglish,
      languageNameNative: version.languageNameNative ?? "",
      status: version.status,
    });
    setEditingId(version.id);
    setErrorMsg(null);
  };

  const handleSubmit = async () => {
    const values = form();
    const id = editingId();
    setErrorMsg(null);
    try {
      await (id
        ? updateMutation.mutateAsync({
            id,
            name: values.name,
            abbr: values.abbr,
            languageIso2: values.languageIso2,
            languageIso3: values.languageIso3,
            languageNameEnglish: values.languageNameEnglish,
            languageNameNative: values.languageNameNative || null,
            status: values.status,
          })
        : createMutation.mutateAsync({
            name: values.name,
            abbr: values.abbr,
            languageIso2: values.languageIso2,
            languageIso3: values.languageIso3,
            languageNameEnglish: values.languageNameEnglish,
            languageNameNative: values.languageNameNative || null,
            status: values.status,
          }));
      setEditingId(null);
      await catalogQuery.refetch();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    }
  };

  const handleDelete = async (id: string) => {
    setErrorMsg(null);
    try {
      await deleteMutation.mutateAsync({ id });
      await catalogQuery.refetch();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    }
  };

  const handleUpload = async (versionId: string, file: File) => {
    setUploadingId(versionId);
    setErrorMsg(null);
    try {
      const formData = new FormData();
      formData.append("versionId", versionId);
      formData.append("file", file);

      const response = await fetch(`${API_BASE}/api/admin/bible/uploadFile`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      await catalogQuery.refetch();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setUploadingId(null);
    }
  };

  const formField = (label: string, key: keyof VersionFormValues, placeholder: string) => (
    <div class="space-y-1">
      <label
        for={`bible-version-${key}`}
        class="text-2xs font-bold text-muted-foreground uppercase tracking-wide"
      >
        {label}
      </label>
      <input
        id={`bible-version-${key}`}
        type="text"
        value={form()[key]}
        onInput={(e) => setForm((prev) => ({ ...prev, [key]: e.currentTarget.value }))}
        placeholder={placeholder}
        class="w-full bg-background border border-border/50 rounded-xl px-3 py-2 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/25 transition-all"
      />
    </div>
  );

  return (
    <div class="space-y-6">
      {/* Header */}
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="p-2.5 rounded-xl bg-primary/10 border border-primary/20 text-primary">
            <BookOpen class="h-5 w-5" />
          </div>
          <div>
            <h1 class="text-xl font-bold tracking-tight">{t(msg`Bible Versions`)}</h1>
            <p class="text-xs text-muted-foreground mt-0.5">
              {t(msg`Manage the catalog of translations and their offline database files.`)}
            </p>
          </div>
        </div>
        <Button onClick={openCreate} class="gap-2">
          <Plus class="h-4 w-4" />
          {t(msg`New version`)}
        </Button>
      </div>

      <Show when={errorMsg()}>
        <p class="text-xs text-red-500 bg-red-500/10 border border-red-500/25 rounded-xl p-3">
          {errorMsg()}
        </p>
      </Show>

      {/* Create/Edit form */}
      <Show when={editingId() !== null}>
        <div class="p-5 rounded-2xl border border-border/40 bg-card/40 space-y-4">
          <div class="flex items-center justify-between">
            <h2 class="text-sm font-bold">
              {editingId() === "" ? t(msg`New version`) : t(msg`Edit version`)}
            </h2>
            <button
              type="button"
              onClick={() => setEditingId(null)}
              class="p-1.5 hover:bg-muted text-muted-foreground rounded-lg"
            >
              <X class="w-4 h-4" />
            </button>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* eslint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n) */}
            {formField(t(msg`Name`), "name", "Reina Valera 1909")}
            {formField(t(msg`Abbreviation`), "abbr", "rv09")}
            {formField("ISO2", "languageIso2", "es")}
            {formField("ISO3", "languageIso3", "spa")}
            {formField(t(msg`Language (English)`), "languageNameEnglish", "Spanish")}
            {/* eslint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n) */}
            {formField(t(msg`Language (native)`), "languageNameNative", "Español")}
          </div>
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <label
                for="bible-version-status"
                class="text-2xs font-bold text-muted-foreground uppercase"
              >
                {t(msg`Status`)}
              </label>
              <Select
                id="bible-version-status"
                aria-label={t(msg`Status`)}
                value={form().status}
                onChange={(value) => setForm((prev) => ({ ...prev, status: value }))}
                options={[
                  { value: "draft", label: "draft" },
                  { value: "published", label: "published" },
                ]}
              />
            </div>
            <Button
              onClick={() => {
                void handleSubmit();
              }}
              disabled={!form().name.trim() || !form().abbr.trim()}
            >
              {t(msg`Save`)}
            </Button>
          </div>
        </div>
      </Show>

      {/* Versions list */}
      <Show
        when={!catalogQuery.isLoading}
        fallback={
          <div class="space-y-3">
            <Skeleton class="h-20 rounded-2xl" />
            <Skeleton class="h-20 rounded-2xl" />
          </div>
        }
      >
        <div class="space-y-3">
          <For
            each={catalogQuery.data?.versions ?? []}
            fallback={
              <p class="text-sm text-muted-foreground text-center py-10 border border-dashed border-border/40 rounded-2xl">
                {t(msg`No Bible versions registered yet.`)}
              </p>
            }
          >
            {(version) => {
              const file = () => filesByVersion().get(version.id);
              const isDeleted = () => Boolean(version.deletedAt);
              return (
                <div
                  class="flex flex-wrap items-center gap-4 p-5 rounded-2xl border border-border/30 bg-card/40"
                  classList={{ "opacity-50": isDeleted() }}
                >
                  <div class="shrink-0 w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                    <span class="text-xs font-black text-primary uppercase">{version.abbr}</span>
                  </div>

                  <div class="flex-1 min-w-40">
                    <div class="flex items-center gap-2">
                      <span class="text-sm font-bold">{version.name}</span>
                      <span class="text-2xs font-semibold px-1.5 py-0.5 rounded-full bg-muted/60 text-muted-foreground uppercase">
                        {version.languageIso2}
                      </span>
                      <span
                        class="text-2xs font-semibold px-1.5 py-0.5 rounded-full uppercase"
                        classList={{
                          "bg-success/10 text-success": version.status === "published",
                          "bg-warning/10 text-warning": version.status !== "published",
                        }}
                      >
                        {isDeleted() ? t(msg`deleted`) : version.status}
                      </span>
                    </div>
                    <p class="text-2xs text-muted-foreground mt-0.5">
                      <Show when={file()} fallback={t(msg`No database file uploaded.`)}>
                        {(f) => (
                          <span>
                            {f().fileType} · {f().relativePath}
                          </span>
                        )}
                      </Show>
                    </p>
                  </div>

                  <div class="flex items-center gap-2 shrink-0">
                    {/* Upload database file */}
                    <label
                      class="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold border border-border/40 bg-background hover:bg-muted/40 transition-all cursor-pointer"
                      classList={{ "opacity-50 pointer-events-none": uploadingId() === version.id }}
                    >
                      <FileUp class="w-3.5 h-3.5" />
                      <span>
                        {uploadingId() === version.id ? t(msg`Uploading...`) : t(msg`Upload DB`)}
                      </span>
                      <input
                        type="file"
                        accept=".db,.sqlite,.sqlite3"
                        class="hidden"
                        onChange={(e) => {
                          const selected = e.currentTarget.files?.[0];
                          if (selected) {
                            void handleUpload(version.id, selected);
                          }
                          e.currentTarget.value = "";
                        }}
                      />
                    </label>

                    <button
                      type="button"
                      onClick={() => {
                        openEdit(version);
                      }}
                      class="p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                      title={t(msg`Edit version`)}
                    >
                      <Pencil class="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        void handleDelete(version.id);
                      }}
                      disabled={isDeleted()}
                      class="p-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-40"
                      title={t(msg`Delete version`)}
                    >
                      <Trash2 class="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            }}
          </For>
        </div>
      </Show>
    </div>
  );
}
